import React, { ReactElement, useEffect } from 'react';

import { Box, DialogTitle, DialogContent, Typography, Grid2, IconButton } from '@mui/material';
import { clearBareGoods } from '../../../redux/actions/orderActions';
import { useAppDispatch } from '../../../hooks/hooks';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/reducers';
import { SaveEntityStatus } from '../../../redux/reducers/common';
import { importSites } from '../../../redux/actions/siteActions';
import OrderFormGoods from './OrderFormGoods';
import { importArticles } from '../../../redux/actions/articleActions';
import {
    clearDemandOrder,
    createNewEmptyDemandOrder,
    importDemandOrder,
    saveDemandOrder,
    setDemandOrderDestinationSite,
} from '../../../redux/actions/demandOrderActions';
import { CompleteOrderType } from '../../../common/Types';
import SiteAutocomplete from '../../../common/components/autocomplete/SiteAutocomplete';
import CloseIcon from '@mui/icons-material/Close';
import { getSiteOptionString } from '../../../common/components/autocomplete/AutocompleteHelper';
import { SiteType } from '../../../api/ClientApi';

import { FormActions, FormDialogTitle } from '../../../common/components/form/FormPrimitives';

import { ResponsiveDialog } from '../../../common/components/mobile/MobilePrimitives';

type DemandOrderFormProps = {
    closeForm(): void;
    open: boolean;
    demandOrderId?: number;
};

export default function DemandOrderForm(props: DemandOrderFormProps): ReactElement {
    const { open, closeForm, demandOrderId } = props;

    const demandOrder = useSelector((state: RootState) => state.demandOrderForm.demandOrder);
    const saveStatus = useSelector((state: RootState) => state.demandOrderForm.saveStatus);
    const bareGoods = useSelector((state: RootState) => state.goodsToOrderForm.bareGoodsEntities);

    const sites = useSelector((state: RootState) => state.sites.sites);
    const articles = useSelector((state: RootState) => state.articles.articles);

    const dispatch = useAppDispatch();

    useEffect(() => {
        if (open) {
            if (!!demandOrderId && demandOrderId > 0) {
                dispatch(importDemandOrder(demandOrderId));
            } else {
                if (!demandOrder) {
                    dispatch(createNewEmptyDemandOrder());
                }
            }
        }
    }, [dispatch, demandOrderId, open]);

    useEffect(() => {
        if (open) {
            if (!sites) {
                dispatch(importSites());
            }
        }
    }, [dispatch, open]);

    useEffect(() => {
        if (open) {
            if (!articles) {
                dispatch(importArticles());
            }
        }
    }, [dispatch, open]);

    useEffect(() => {
        if (saveStatus === SaveEntityStatus.Saved) {
            handleClose();
        }
    }, [dispatch, saveStatus]);

    const handleSelectDestinationSite = (siteId?: number) => {
        const site = sites?.find((s) => s.id === siteId);

        dispatch(setDemandOrderDestinationSite(site));
    };

    const handleSave = () => {
        dispatch(saveDemandOrder());
    };

    const handleClose = () => {
        dispatch(clearDemandOrder());
        dispatch(clearBareGoods());
        closeForm();
    };

    const isOrderDataValid =
        !!demandOrder &&
        !!demandOrder.destinationSiteId &&
        demandOrder.destinationSiteId > 0 &&
        !!bareGoods &&
        bareGoods.filter((bg) => !!bg.article && bg.orderedQuantity > 0).length > 0 &&
        bareGoods.filter((bg) => !!bg.article && bg.orderedQuantity <= 0).length === 0;

    return (
        <ResponsiveDialog fullScreen scroll="paper" open={open} onClose={handleClose}>
            <FormDialogTitle onClose={handleClose}>
                Trebovanje - {!demandOrderId ? 'kreiranje' : 'izmena'}
            </FormDialogTitle>
            <DialogContent dividers sx={{ px: { xs: 2, sm: 5 }, pt: { xs: 3, sm: 5 } }}>
                <Grid2 container spacing={2} style={{ margin: 'auto' }}>
                    {!demandOrderId ? (
                        <Grid2 size={{ xs: 12, sm: 6 }}>
                            <SiteAutocomplete
                                isRequired
                                selectSite={handleSelectDestinationSite}
                                sites={sites?.filter((s) => s.siteType === SiteType.ConstructionSite) ?? []}
                                selectedSiteId={demandOrder?.destinationSiteId}
                                title="Odredište - skladište"
                            />
                        </Grid2>
                    ) : (
                        <Box>
                            <Typography variant="body1">
                                Odredište - skladište:{' '}
                                <span style={{ fontWeight: 'bold' }}>
                                    {getSiteOptionString(demandOrder?.destinationSite)}
                                </span>
                            </Typography>
                        </Box>
                    )}
                    <Grid2 size={{ xs: 12 }}>
                        <OrderFormGoods completeOrderType={CompleteOrderType.Demand} isOriginChosen isReceipt={false} />
                    </Grid2>
                    <Grid2 size={{ xs: 0, sm: 12 }} style={{ height: 15, margin: 0, padding: 0 }} />
                    {!isOrderDataValid && (
                        <Grid2 size={{ xs: 12 }}>
                            <Typography color="error">*unesite sva obavezna polja</Typography>
                        </Grid2>
                    )}
                    <FormActions
                        onCancel={handleClose}
                        onSubmit={handleSave}
                        submitLabel="Sačuvaj"
                        disabled={!isOrderDataValid || saveStatus === SaveEntityStatus.Pending}
                        pending={saveStatus === SaveEntityStatus.Pending}
                    />
                </Grid2>
            </DialogContent>
        </ResponsiveDialog>
    );
}
